import { useEffect, useState } from "react";
import {
  CheckIcon,
  CheckCircleIcon,
  NoSymbolIcon,
} from "@heroicons/react/20/solid";
import { DateTime } from "luxon";
import Modal from "../Shared/Modal";
import { useUserPreferences } from "../../hooks/useUserPreferences";

const PastConfirmedModal = ({
  open,
  onClose,
  appointments = [],
  onMarkCompleted,
  onMarkNoShow,
  onClientClick,
  onMarkAllCompleted,
  bulkProgress,
}) => {
  const prefs = useUserPreferences();
  const { is12HourClock } = prefs;
  const tz = prefs.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const [pendingNoShow, setPendingNoShow] = useState(null);

  useEffect(() => {
    if (!open) {
      setConfirmingBulk(false);
      setPendingNoShow(null);
    }
  }, [open]);

  const formatTime = (isoTime) => {
    if (!isoTime) return "";
    const format = is12HourClock ? "MMM d, h:mm a" : "MMM d, HH:mm";
    return DateTime.fromISO(isoTime).setZone(tz).toFormat(format);
  };

  const handleMarkAll = () => {
    const ids = appointments.map((a) => a.id);
    setConfirmingBulk(false);
    onMarkAllCompleted(ids);
  };

  const bulkActive = !!bulkProgress;
  const progressPct = bulkProgress
    ? Math.min(100, Math.round((bulkProgress.done / Math.max(bulkProgress.total, 1)) * 100))
    : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Update past appointments"
      size="md"
    >
      {appointments.length === 0 ? (
        <div className="text-center py-10">
          <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-signal-green-50">
            <CheckCircleIcon className="size-7 text-signal-green-500" />
          </div>
          <p className="text-base font-medium text-ink-900">All caught up</p>
          <p className="mt-1 text-sm text-ink-500">
            No past appointments need a status update.
          </p>
          <button
            onClick={onClose}
            className="mt-6 inline-flex items-center rounded-pill bg-accent-500 px-4 py-2 text-sm font-semibold text-cream-50 hover:bg-accent-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500 transition-colors"
          >
            Close
          </button>
        </div>
      ) : (
        <>
          {/* Bulk toolbar */}
          <div className="mb-4 pb-4 border-b border-ink-100">
            {bulkActive ? (
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-ink-900">
                    Marking appointments as completed…
                  </span>
                  <span className="font-mono tabular text-ink-500">
                    {bulkProgress.done} / {bulkProgress.total}
                  </span>
                </div>
                <div
                  className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-ink-100"
                  role="progressbar"
                  aria-valuenow={bulkProgress.done}
                  aria-valuemin={0}
                  aria-valuemax={bulkProgress.total}
                >
                  <div
                    className="h-full bg-signal-green-500 transition-all duration-300 ease-editorial"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            ) : confirmingBulk ? (
              <div className="rounded-card bg-cream-100 p-3">
                <p className="text-sm text-ink-700">
                  Mark {appointments.length} appointment
                  {appointments.length === 1 ? "" : "s"} as completed. Revenue
                  and stats will update.
                </p>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setConfirmingBulk(false)}
                    className="inline-flex items-center rounded-pill bg-white px-3 py-1.5 text-sm font-medium text-ink-700 ring-1 ring-inset ring-ink-200 hover:bg-cream-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMarkAll}
                    className="inline-flex items-center rounded-pill bg-signal-green-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-signal-green-500"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmingBulk(true)}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-pill bg-signal-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-signal-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-green-600 transition-colors sm:w-auto"
              >
                <CheckIcon className="size-4" />
                Mark all {appointments.length} as completed
              </button>
            )}
          </div>

          {/* Appointment list */}
          <ul
            className={`divide-y divide-ink-100 ${
              bulkActive ? "pointer-events-none opacity-60" : ""
            }`}
            aria-busy={bulkActive}
          >
            {appointments.map((appointment) => {
              const isConfirmingNoShow = pendingNoShow === appointment.id;
              return (
                <li
                  key={appointment.id}
                  className="flex items-center justify-between gap-3 py-3 first:pt-0"
                >
                  <div className="min-w-0 flex-1">
                    <button
                      onClick={() => onClientClick(appointment.clientId)}
                      className="text-sm font-medium text-ink-900 hover:text-accent-500 hover:underline transition-colors duration-150 truncate text-left"
                    >
                      {appointment.clientFullName}
                    </button>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-ink-500">
                      <span className="font-mono tabular">
                        {formatTime(appointment.startTime)}
                      </span>
                      {appointment.services?.length > 0 && (
                        <>
                          <span aria-hidden="true" className="text-ink-300">
                            ·
                          </span>
                          <span className="truncate">
                            {appointment.services.map((s) => s.name).join(", ")}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isConfirmingNoShow ? (
                      <>
                        <button
                          onClick={() => {
                            setPendingNoShow(null);
                            onMarkNoShow(appointment.id);
                          }}
                          className="inline-flex items-center rounded-pill bg-signal-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-signal-red-500"
                          aria-label={`Confirm no-show for ${appointment.clientFullName}`}
                        >
                          Confirm no-show
                        </button>
                        <button
                          onClick={() => setPendingNoShow(null)}
                          className="rounded-md p-1.5 text-ink-500 hover:bg-cream-100"
                          aria-label="Cancel no-show"
                        >
                          <span aria-hidden="true" className="text-xs">
                            Cancel
                          </span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => onMarkCompleted(appointment.id)}
                          className="rounded-md p-1.5 text-ink-400 hover:text-signal-green-600 hover:bg-signal-green-50 transition-colors"
                          aria-label={`Mark ${appointment.clientFullName} as completed`}
                          title="Mark completed"
                        >
                          <CheckIcon className="size-4" />
                        </button>
                        <button
                          onClick={() => setPendingNoShow(appointment.id)}
                          className="rounded-md p-1.5 text-ink-400 hover:text-signal-red-600 hover:bg-signal-red-50 transition-colors"
                          aria-label={`Mark ${appointment.clientFullName} as no-show`}
                          title="Mark no-show / cancelled"
                        >
                          <NoSymbolIcon className="size-4" />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Modal>
  );
};

export default PastConfirmedModal;
